import { BadgeDollarSign, ChartLine } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import { VendaItem } from "shared/components/vendas/VendaItem";
import { useVendas } from "shared/hooks/useVendas";
import { formatarMoeda, reaisParaCentavos } from "shared/utils/money";
import styles from "./MinhasVendas.module.scss";

export const MinhasVendas = () => {
  const navigate = useNavigate();
  const { vendas, loading } = useVendas();
  const hoje = new Date();
  const [tabAtiva, setTabAtiva] = useState<"hoje" | "semana" | "mes" | "ano">("hoje");
  const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth());
  const [anoSelecionado, setAnoSelecionado] = useState(hoje.getFullYear());

  const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho",
    "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

  const mesesAbreviados = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago",
    "Set", "Out", "Nov", "Dez"];

  /* ================================
     CONVERSÃO DE DATA
  ================================ */

  const getDate = (data: any) => {
    if (!data) return new Date();
    if (data.seconds) return new Date(data.seconds * 1000);
    return new Date(data);
  };

  /* ================================
     ANOS DISPONÍVEIS
  ================================ */

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<number>();
    anos.add(new Date().getFullYear());

    vendas.forEach((venda) => {
      const data = getDate(venda.data);
      if (!Number.isNaN(data.getTime())) anos.add(data.getFullYear());
    });

    return Array.from(anos).sort((a, b) => b - a);
  }, [vendas]);

  /* ================================
     FILTRO
  ================================ */

  const vendasFiltradas = useMemo(() => {
    const agora = new Date();

    return vendas.filter((venda) => {
      const data = getDate(venda.data);

      if (tabAtiva === "hoje") return data.toDateString() === agora.toDateString();

      if (tabAtiva === "semana") {
        const inicioSemana = new Date(agora);
        inicioSemana.setDate(agora.getDate() - 7);
        return data >= inicioSemana && data <= agora;
      }

      if (tabAtiva === "mes") {
        return data.getMonth() === mesSelecionado && data.getFullYear() === anoSelecionado;
      }

      if (tabAtiva === "ano") return data.getFullYear() === anoSelecionado;

      return true;
    });
  }, [vendas, tabAtiva, mesSelecionado, anoSelecionado]);

  /* ================================
     RESUMO DINÂMICO
  ================================ */

  const resumo = useMemo(() => {
    const faturamento = vendasFiltradas.reduce((acc, venda) => acc + (venda.total || 0), 0);
    const pedidos = vendasFiltradas.length;
    const ticketMedio = pedidos > 0 ? faturamento / pedidos : 0;

    return { faturamento, pedidos, ticketMedio };
  }, [vendasFiltradas]);

  const vazio = vendasFiltradas.length === 0;

  /* ================================
     TROCA DE ABA
  ================================ */

  const selecionarTab = (tab: "hoje" | "semana" | "mes" | "ano") => {
    setTabAtiva(tab);

    if (tab === "mes") {
      setMesSelecionado(hoje.getMonth());
      setAnoSelecionado(hoje.getFullYear());
    }

    if (tab === "ano") setAnoSelecionado(hoje.getFullYear());
  };

  if (loading) return <p>Carregando...</p>;

  return (
    <PageContainer title="Minhas Vendas" icon={<BadgeDollarSign />} showDesktopSearch={false} showLogo
      logoPosition="left" logo={<img src="/imagens/menuLogo.png" alt="Move" />}
      contentClassName={styles.whiteContent}
      actions={<button className={styles.btnRelatorio} onClick={() => navigate("/relatorios")}>
        Relatório <ChartLine />
      </button>}
    >
      <div className={styles.containerResumo}>
        <div className={styles.tabs}>
          <button className={tabAtiva === "hoje" ? styles.active : ""} onClick={() => selecionarTab("hoje")}>
            Hoje
          </button>

          <button className={tabAtiva === "semana" ? styles.active : ""} onClick={() => selecionarTab("semana")}>
            Semana
          </button>

          <div className={`${styles.tabPeriodo} ${tabAtiva === "mes" ? styles.active : ""}`}>
            <button type="button" onClick={() => selecionarTab("mes")}>
              {tabAtiva !== "mes" && <span>Mês</span>}

              {tabAtiva === "mes" && (
                <>
                  <div className={styles.seletoresDesktop}>
                    <select value={mesSelecionado} onChange={(e) => setMesSelecionado(Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}>
                      {meses.map((mes, index) => (
                        <option key={mes} value={index}>{mes}</option>
                      ))}
                    </select>

                    <select value={anoSelecionado} onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}>
                      {anosDisponiveis.map((ano) => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.seletoresMobile}>
                    <select value={mesSelecionado} onChange={(e) => setMesSelecionado(Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}>
                      {mesesAbreviados.map((mes, index) => (
                        <option key={mes} value={index}>{mes}</option>
                      ))}
                    </select>

                    <span className={styles.separadorPeriodo}>/</span>

                    <select value={anoSelecionado} onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}>
                      {anosDisponiveis.map((ano) => (
                        <option key={ano} value={ano}>{String(ano).slice(-2)}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </button>
          </div>

          <div className={`${styles.tabPeriodo} ${tabAtiva === "ano" ? styles.active : ""}`}>
            <button type="button" onClick={() => selecionarTab("ano")}>
              {tabAtiva !== "ano" && <span>Ano</span>}

              {tabAtiva === "ano" && (
                <>
                  <div className={styles.seletorAnoDesktop}>
                    <select value={anoSelecionado} onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                      onClick={(e) => e.stopPropagation()}>
                      {anosDisponiveis.map((ano) => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </button>
          </div>
        </div>

        <div className={styles.resumoInfo}>
          <div>
            <span>FATURAMENTO</span>
            <p>{formatarMoeda(reaisParaCentavos(resumo.faturamento))}</p>
          </div>

          <div>
            <span>PEDIDOS</span>
            <p>{resumo.pedidos}</p>
          </div>

          <div>
            <span>TICKET MÉDIO</span>
            <p>{formatarMoeda(reaisParaCentavos(resumo.ticketMedio))}</p>
          </div>
        </div>
      </div>

      <div className={styles.listaVendas}>
        {vazio ? (
          <div className={styles.vendasVazio}>
            <div className={styles.containerImgVazio}>
              <img src="/imagens/vendasVazio.png" alt="Sem vendas" />
            </div>

            <p className={styles.fraseVazio}>
              Você ainda
              <br /> não tem nenhuma venda
            </p>
          </div>
        ) : (
          vendasFiltradas.map((venda) => <VendaItem key={venda.id} venda={venda} />)
        )}
      </div>
    </PageContainer>
  );
};